/**
 * Listas do módulo Canopus, lidas das tabelas que o sync do AFV preenche
 * (canopus_vendas, canopus_clientes, canopus_comissoes — leitura só super admin,
 * migration 245). Cada sync troca o retrato inteiro; `sync_at` diz de quando é.
 */
import { useMemo, useState } from 'react';
import { useGetList } from 'ra-core';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { formatBRL } from '@/lib/format';
import { CanopusVendasApp } from './CanopusVendasApp';
import { CaixaTabela } from '@/components/ui/table';
import { CabecalhoPagina } from '../bento/ui';
import { Card } from '@/components/ui/card';

type Coluna<T> = { titulo: string; valor: (l: T) => React.ReactNode; direita?: boolean; busca?: (l: T) => string };

export const dataHora = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const dataBr = (iso?: string | null) => {
  const m = String(iso ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(iso ?? '—');
};

function CanopusTabela<T extends { id: number; sync_at?: string }>({
  titulo, descricao, recurso, ordem, sentido = 'DESC', colunas, vazio, topo, comoSecao = false,
}: {
  titulo: string; descricao: string; recurso: string; ordem: string; sentido?: 'ASC' | 'DESC'; colunas: Coluna<T>[]; vazio: string;
  /** Bloco entre o cabeçalho e a tabela (os percentuais da Carteira). */
  topo?: React.ReactNode;
  /** Segunda lista da página (Vendas no AFV): título no próprio cartão, sem o cabeçalho de página. */
  comoSecao?: boolean;
}) {
  const [busca, setBusca] = useState('');
  const { data, isPending, error } = useGetList<T>(recurso, {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: ordem, order: sentido },
  });
  const linhas = useMemo(() => {
    const termo = busca.trim().toUpperCase();
    if (!termo) return data || [];
    return (data || []).filter((l) => colunas.some((c) => String(c.busca?.(l) ?? '').toUpperCase().includes(termo)));
  }, [data, busca, colunas]);

  return (
    <div className="space-y-4">
      {!comoSecao && (
        <CabecalhoPagina
          titulo={titulo}
          descricao={<>{descricao}{data?.[0]?.sync_at ? ` Posição do AFV em ${dataHora(data[0].sync_at)}.` : ''}</>}
        />
      )}
      {topo}
      {/* Premium North: busca dentro do cartão, cabeçalho claro e células que
          quebram linha — a tabela não rola de lado. */}
      <Card className="gap-0 overflow-hidden py-0">
        {comoSecao && (
          <div className="border-b border-border px-4 py-3">
            <h2 className="text-[13.5px] font-semibold text-marinho">{titulo}</h2>
            <p className="text-xs text-muted-foreground">
              {descricao}{data?.[0]?.sync_at ? ` Posição do AFV em ${dataHora(data[0].sync_at)}.` : ''}
            </p>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
          <Input className="w-72" placeholder="Buscar" value={busca} onChange={(e) => setBusca(e.target.value)} />
          {!isPending && !error && (
            <span className="ml-auto text-xs tabular-nums text-muted-foreground">{linhas.length}</span>
          )}
        </div>
        {isPending ? (
          <p className="flex items-center gap-2 px-4 py-6 text-sm"><Spinner className="h-4 w-4" />Carregando…</p>
        ) : error ? (
          <p className="px-4 py-6 text-sm text-erro">Não foi possível carregar os dados.</p>
        ) : linhas.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-muted-foreground">{vazio}</p>
        ) : (
          <CaixaTabela>
            <table className="w-full text-sm">
              <thead className="text-left">
                <tr className="border-b border-border">
                  {colunas.map((c) => (
                    <th key={c.titulo} className={`h-10 px-3 text-xs font-medium text-muted-foreground ${c.direita ? 'text-right' : ''}`}>{c.titulo}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {linhas.map((l) => (
                  <tr key={l.id} className="border-b border-border/70 last:border-b-0 hover:bg-linha-hover dark:hover:bg-muted/40">
                    {colunas.map((c) => (
                      <td key={c.titulo} className={`px-3 py-2.5 align-top ${c.direita ? 'text-right tabular-nums' : ''}`}>{c.valor(l)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </CaixaTabela>
        )}
      </Card>
    </div>
  );
}

type Venda = { id: number; id_pedido: number | null; id_contrato: string | null; data_contrato: string | null; cliente: string | null; valor_bem: number | null; status: string | null; nome_vendedor: string | null; sync_at?: string };
type Cliente = { id: number; id_cliente: number | null; nome_razao: string | null; cpf_cnpj: string | null; sync_at?: string };
type Cota = { id: number; situacao: 'em_dia' | 'atrasada'; data_alocacao: string | null; status: string | null; nome_consorciado: string | null; grupo: string | null; cota: string | null; telefone: string | null; cidade: string | null; valor_credito: number | null; valor_atraso: number | null; sync_at?: string };
type Comissao = { id: number; grupo: string | null; cota: string | null; valor_comissao: number | null; nr_parcela: string | null; data_encerramento: string | null; cod_ponto_venda: string | null; sync_at?: string };

const VENDAS: Coluna<Venda>[] = [
  { titulo: 'Data', valor: (l) => dataBr(l.data_contrato) },
  { titulo: 'Pedido', valor: (l) => l.id_pedido ?? '—', busca: (l) => String(l.id_pedido ?? '') },
  { titulo: 'Cliente', valor: (l) => l.cliente || '—', busca: (l) => l.cliente || '' },
  { titulo: 'Valor do bem', valor: (l) => (l.valor_bem != null ? formatBRL(Number(l.valor_bem)) : '—'), direita: true },
  { titulo: 'Contrato', valor: (l) => l.id_contrato || '—', busca: (l) => l.id_contrato || '' },
  { titulo: 'Status', valor: (l) => l.status || '—', busca: (l) => l.status || '' },
];

const CLIENTES: Coluna<Cliente>[] = [
  { titulo: 'Nome / razão social', valor: (l) => l.nome_razao || '—', busca: (l) => l.nome_razao || '' },
  { titulo: 'CPF / CNPJ', valor: (l) => l.cpf_cnpj || '—', busca: (l) => l.cpf_cnpj || '' },
  { titulo: 'Código no AFV', valor: (l) => l.id_cliente ?? '—', direita: true },
];

const COMISSOES: Coluna<Comissao>[] = [
  { titulo: 'Grupo', valor: (l) => l.grupo || '—', busca: (l) => l.grupo || '' },
  { titulo: 'Cota', valor: (l) => l.cota || '—', busca: (l) => l.cota || '' },
  { titulo: 'Parcela', valor: (l) => l.nr_parcela || '—' },
  { titulo: 'Encerramento', valor: (l) => l.data_encerramento || '—' },
  { titulo: 'Valor', valor: (l) => (l.valor_comissao != null ? formatBRL(Number(l.valor_comissao)) : '—'), direita: true },
];

export const CanopusVendasPage = () => (
  <div className="space-y-4">
    <CanopusVendasApp />
    <CanopusTabela<Venda> comoSecao titulo="Vendas no AFV" descricao="Pedidos e vendas da North no AFV, inclusive os feitos fora do app." recurso="canopus_vendas" ordem="data_contrato" colunas={VENDAS} vazio="Nenhuma venda Canopus no último sync." />
  </div>
);

export const CanopusClientesPage = () => (
  <CanopusTabela<Cliente> titulo="Clientes Canopus" descricao="Clientes cadastrados pela North no AFV." recurso="canopus_clientes" ordem="nome_razao" sentido="ASC" colunas={CLIENTES} vazio="Nenhum cliente Canopus no último sync." />
);

export const CanopusComissoesPage = () => (
  <CanopusTabela<Comissao> titulo="Comissões Canopus" descricao="Comissões a receber da Canopus." recurso="canopus_comissoes" ordem="data_encerramento" colunas={COMISSOES} vazio="Nenhuma comissão a receber no último sync." />
);

const dinheiro = (v: number | null) => (v != null ? formatBRL(Number(v)) : '—');

const CARTEIRA: Coluna<Cota>[] = [
  { titulo: 'Situação', valor: (l) => (l.situacao === 'atrasada' ? <span className="inline-flex rounded-[4px] bg-erro-fundo px-2 py-0.5 text-[11.5px] font-semibold text-erro">{l.status || 'Em atraso'}</span> : <span className="inline-flex rounded-[4px] bg-ok-fundo px-2 py-0.5 text-[11.5px] font-semibold text-ok">Em dia</span>), busca: (l) => l.situacao === 'atrasada' ? 'atraso' : 'em dia' },
  { titulo: 'Consorciado', valor: (l) => l.nome_consorciado || '—', busca: (l) => l.nome_consorciado || '' },
  { titulo: 'Grupo/cota', valor: (l) => `${l.grupo || '—'}/${l.cota || '—'}`, busca: (l) => `${l.grupo}/${l.cota}` },
  { titulo: 'Alocação', valor: (l) => dataBr(l.data_alocacao) },
  { titulo: 'Telefone', valor: (l) => l.telefone || '—', busca: (l) => l.telefone || '' },
  { titulo: 'Cidade', valor: (l) => l.cidade || '—', busca: (l) => l.cidade || '' },
  { titulo: 'Crédito', valor: (l) => dinheiro(l.valor_credito), direita: true },
  { titulo: 'Em atraso', valor: (l) => dinheiro(l.valor_atraso), direita: true },
];

type Indicadores = { id: number; perc_adimplencia: number | null; perc_atraso: number | null; perc_cancelamento: number | null; sync_at: string };
const pct = (v: number | null | undefined) => (v != null ? `${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%` : '—');

function IndicadoresCarteira() {
  const { data } = useGetList<Indicadores>('canopus_carteira_indicadores', {
    pagination: { page: 1, perPage: 1 },
    sort: { field: 'sync_at', order: 'DESC' },
  });
  const i = data?.[0];
  return (
    // Os três percentuais numa faixa (Premium North).
    <div className="cartao border-t-ouro grid grid-cols-1 overflow-hidden bg-card sm:grid-cols-3">
      {([['Adimplência', i?.perc_adimplencia], ['Em atraso', i?.perc_atraso], ['Cancelamento', i?.perc_cancelamento]] as const).map(([t, v]) => (
        <div key={t} className="border-b border-border px-4 py-3.5 last:border-b-0 sm:border-b-0 sm:border-l sm:first:border-l-0">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">{t}</div>
          <div className="mt-1.5 text-[22px] font-bold leading-none tabular-nums text-marinho">{pct(v)}</div>
        </div>
      ))}
    </div>
  );
}

export const CanopusCarteiraPage = () => (
  <CanopusTabela<Cota> titulo="Carteira Canopus" descricao="Cotas da North na Canopus, em dia e em atraso (Gestão de Carteira do AFV)." recurso="canopus_carteira" ordem="valor_atraso" colunas={CARTEIRA} vazio="Nenhuma cota na carteira no último sync." topo={<IndicadoresCarteira />} />
);

